export type DownloadPlatform = 'win' | 'arm' | 'intel';

interface NavigatorUAData {
  platform?: string;
  architecture?: string;
}

type SystemSettingLike = Record<string, unknown> | undefined;

function isValidDownloadUrl(url?: string): url is string {
  if (!url) {
    return false;
  }

  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

function collectHintText(input: unknown, result: string[], depth = 0): void {
  if (depth > 4 || input == null) {
    return;
  }
  if (typeof input === 'string' || typeof input === 'number' || typeof input === 'boolean') {
    result.push(String(input).toLowerCase());
    return;
  }
  if (Array.isArray(input)) {
    input.forEach((item) => collectHintText(item, result, depth + 1));
    return;
  }
  if (typeof input === 'object') {
    Object.values(input as Record<string, unknown>).forEach((value) => {
      collectHintText(value, result, depth + 1);
    });
  }
}

function getWebGLRenderer(): string {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) {
      return '';
    }
    const debugInfo = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) {
      return '';
    }
    const renderer = (gl as WebGLRenderingContext).getParameter(
      (debugInfo as { UNMASKED_RENDERER_WEBGL: number }).UNMASKED_RENDERER_WEBGL,
    );
    return String(renderer || '').toLowerCase();
  } catch {
    return '';
  }
}

export function getCurrentDownloadPlatform(systemSetting?: SystemSettingLike): DownloadPlatform {
  const nav = navigator as Navigator & { userAgentData?: NavigatorUAData };
  const platform = (nav.userAgentData?.platform || navigator.platform || '').toLowerCase();
  const architecture = (nav.userAgentData?.architecture || '').toLowerCase();
  const ua = navigator.userAgent.toLowerCase();

  if (platform.includes('win')) {
    return 'win';
  }

  if (platform.includes('mac')) {
    const hints: string[] = [architecture, ua];
    collectHintText(systemSetting, hints);
    const hintText = hints.join(' ');

    if (
      /arm64|aarch64|apple\s*silicon|apple\s*m\d+/i.test(hintText)
    ) {
      return 'arm';
    }

    const renderer = getWebGLRenderer();
    if (/apple\s*m\d+/i.test(renderer)) {
      return 'arm';
    }

    if (/x64|x86_64|amd64|\bintel\b/i.test(hintText)) {
      return 'intel';
    }

    if (/intel/i.test(renderer)) {
      return 'intel';
    }

    if (
      architecture.includes('arm') ||
      ua.includes('arm64') ||
      ua.includes('aarch64') ||
      ua.includes('apple silicon')
    ) {
      return 'arm';
    }
    return 'intel';
  }

  return 'intel';
}

export function pickDownloadUrlByPlatform(
  urls: { win?: string; arm?: string; intel?: string },
  platform: DownloadPlatform,
): string {
  const preferredOrder: DownloadPlatform[] =
    platform === 'win' ? ['win', 'intel', 'arm'] : [platform, 'intel', 'arm', 'win'];

  for (const key of preferredOrder) {
    const candidate = urls[key];
    if (isValidDownloadUrl(candidate)) {
      return candidate;
    }
  }

  return '';
}

/**
 * 仅获取当前平台的下载链接，不做降级 fallback。
 * 用于入口展示：只有在当前平台有可用链接时才展示下载按钮。
 */
export function getDownloadUrlForCurrentPlatform(
  urls: { win?: string; arm?: string; intel?: string },
  platform: DownloadPlatform,
): string {
  const candidate = urls[platform];
  return isValidDownloadUrl(candidate) ? candidate : '';
}
